import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDashboardStore } from "../../stores/dashboardStore";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, horizontalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

const SortableWidget = ({ widget }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: widget.id });
  const unpinWidget = useDashboardStore(state => state.unpinWidget);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative bg-white border rounded-xl p-4 flex flex-col gap-2 min-w-[200px] h-[120px] shadow-sm ${isDragging ? "shadow-lg scale-105 opacity-80" : ""}`}
    >
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-2">
          <span className="text-xl">{widget.icon}</span>
          <p className="font-semibold text-gray-800">{widget.title}</p>
        </div>
        <button 
          onClick={() => unpinWidget(widget.id)}
          className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1 rounded-md transition-colors"
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>
      <div className="mt-auto flex justify-center text-gray-400 cursor-grab active:cursor-grabbing hover:text-gray-600" {...attributes} {...listeners}>
        <DragIndicatorIcon />
      </div>
    </div>
  );
};

const EditDashboardModal = () => {
  const { isEditModalOpen, toggleEditModal, pinnedWidgets, availableWidgets, reorderWidgets, pinWidget } = useDashboardStore();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      reorderWidgets(active.id, over.id);
    }
  };

  if (!isEditModalOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b flex justify-between items-center bg-orange-50/50">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Widgets <span className="text-sm font-medium text-orange-600 bg-orange-100 px-2 py-1 rounded-md ml-2">{pinnedWidgets.length} USED</span></h2>
              <p className="text-sm text-gray-500 mt-1">Choose and organize the widgets that appear on your dashboard.</p>
            </div>
            <button onClick={toggleEditModal} className="p-2 hover:bg-gray-100 rounded-full text-gray-500 transition-colors">
              <CloseIcon />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-8 bg-gray-50/30">
            
            {/* Pinned Widgets (Draggable) */}
            <div>
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Your Dashboard Widgets</h3>
              <div className="p-4 bg-gray-50 border border-dashed border-gray-300 rounded-xl">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={pinnedWidgets.map(w => w.id)} strategy={horizontalListSortingStrategy}>
                    <div className="flex flex-wrap gap-4">
                      {pinnedWidgets.map(widget => (
                        <SortableWidget key={widget.id} widget={widget} />
                      ))}
                      {pinnedWidgets.length === 0 && (
                        <p className="text-gray-500 text-sm italic w-full text-center py-4">No widgets pinned. Add some below.</p>
                      )}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            </div>

            {/* Available Widgets */}
            <div>
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Available Widgets</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {availableWidgets.map(widget => (
                  <div key={widget.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-2 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{widget.icon}</span>
                      <p className="font-semibold text-gray-800">{widget.title}</p>
                    </div>
                    <p className="text-sm text-gray-500 flex-1">{widget.description}</p>
                    <button 
                      onClick={() => pinWidget(widget)}
                      className="mt-2 w-full py-1.5 bg-indigo-50 text-indigo-600 font-medium rounded-lg hover:bg-indigo-100 transition-colors text-sm"
                    >
                      + Add to Dashboard
                    </button>
                  </div>
                ))}
                {availableWidgets.length === 0 && (
                  <p className="text-gray-500 text-sm italic col-span-full">All widgets are already on your dashboard!</p>
                )}
              </div>
            </div>

          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default EditDashboardModal;
